import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["item", "onClick", "closeType"],
  _excluded2 = ["moreTitle", "autoplayTime", "render", "isInCollection"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { RecommendationsForLearningContent } from './RecommendationsForLearningContent';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useEffect, useMemo, useState } from 'react';
import { H5 } from 'foundations-components/transitional/components/H5';
import { H2 } from 'foundations-components/transitional/components/H2';
import { getLearningContentItemUrl } from '../../learningContent';
import useNavigate from '../../../hooks/useNavigate';
import UsageTracker from '../../../utils/UsageTracker';
import { useVideoPlayerContext } from '../../video/components/VideoPlayer';
import { useAiRecommendationsForLearningContent } from '../../academyAi/academyAiQueries';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Button } from 'foundations-components/transitional/components/Button';
import RouterButton from 'ui-addon-react-router-dom/RouterButton';
import { H4 } from 'foundations-components/transitional/components/H4';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DEFAULT_AUTOPLAY_TIME = 10;
const useMainRecommendationUrl = item => {
  const {
    recommendations
  } = useAiRecommendationsForLearningContent({
    item,
    size: 1
  });
  const recommendedItem = (recommendations === null || recommendations === void 0 ? void 0 : recommendations.length) && {
    id: recommendations[0].recommendationId,
    type: recommendations[0].recommendationType,
    language: recommendations[0].language
  };
  const url = recommendedItem ? getLearningContentItemUrl(recommendedItem) : null;
  return {
    url,
    recommendedItem
  };
};
const CloseRecommendationsButton = _ref => {
  let {
      item: {
        id,
        language,
        type
      },
      onClick,
      closeType
    } = _ref,
    restProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const handleOnCancel = () => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: `cancel-recommendation-${closeType}`,
      id,
      contentType: type,
      language
    });
    onClick === null || onClick === void 0 || onClick();
  };
  return /*#__PURE__*/_jsx(Button, Object.assign({
    marginBlockStart: "0",
    onClick: handleOnCancel,
    size: closeType === 'mobile' ? 'small' : 'medium',
    use: "secondary"
  }, restProps, {
    children: closeType === 'autoplay' ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.recommendations.seeMore"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.common.close"
    })
  }));
};
const MainRecommendation = ({
  onClickCancel,
  autoplayTime,
  item,
  onItemClick
}) => {
  const {
    url,
    recommendedItem
  } = useMainRecommendationUrl(item);
  const {
    cardModels,
    cardModelsLoading
  } = useLearningContentCardModels({
    items: recommendedItem ? [recommendedItem] : [],
    skip: !recommendedItem
  });
  const navigate = useNavigate();
  const [remainingSeconds, setRemainingSeconds] = useState(autoplayTime);
  const trackInfo = useMemo(() => {
    return {
      id: item.id,
      contentType: item.type,
      language: item.language
    };
  }, [item]);
  useEffect(() => {
    if (!url || cardModelsLoading || !(cardModels !== null && cardModels !== void 0 && cardModels.length)) {
      return;
    }
    let interval = undefined;
    const checkIfAutoplay = () => {
      if (remainingSeconds > 0) return;
      clearInterval(interval);
      if (recommendedItem) onItemClick(recommendedItem, 'autoplay');
      navigate(url);
    };
    interval = setInterval(() => {
      setRemainingSeconds(remainingSeconds - 1);
    }, 1000);
    checkIfAutoplay();
    return () => {
      clearInterval(interval);
    };
  }, [navigate, onItemClick, recommendedItem, trackInfo, remainingSeconds, url, cardModelsLoading, cardModels]);
  if (!url || !recommendedItem || cardModelsLoading || !(cardModels !== null && cardModels !== void 0 && cardModels.length)) return null;
  return /*#__PURE__*/_jsxs(View, {
    inlineSize: "100%",
    maxInlineSize: "500px",
    paddingBlockEnd: "1300",
    paddingBlockStart: "0",
    paddingInline: "1300",
    children: [/*#__PURE__*/_jsx(H2, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.recommendations.recommendedVideos"
      })
    }), /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "400",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.recommendations.upNextTimer",
        options: {
          remainingSeconds
        }
      })
    }), /*#__PURE__*/_jsx(RecommendationsForLearningContent, Object.assign({
      appearsIn: "main-video-recommendation",
      onItemClick: () => onItemClick(recommendedItem, 'main'),
      showDescription: false
    }, item)), /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(RouterButton, {
        onClick: () => onItemClick(recommendedItem, 'watch-now'),
        to: url,
        use: "tertiary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.recommendations.watchNow"
        })
      }), /*#__PURE__*/_jsx(CloseRecommendationsButton, {
        closeType: "autoplay",
        item: item,
        marginInlineStart: "300",
        onClick: onClickCancel
      })]
    })]
  });
};
const MoreRecommendations = ({
  moreTitle,
  onClickCancel,
  item,
  onItemClick
}) => {
  const {
    id,
    language,
    type
  } = item;
  return /*#__PURE__*/_jsxs(View, {
    inlineSize: "100%",
    paddingBlockEnd: "1300",
    paddingBlockStart: "0",
    paddingInline: "1300",
    children: [/*#__PURE__*/_jsx(H4, {
      marginBlockEnd: "400",
      children: moreTitle
    }), /*#__PURE__*/_jsx(RecommendationsForLearningContent, {
      appearsIn: "more-video-recommendations",
      id: id,
      language: language,
      onItemClick: recommendedItem => onItemClick({
        id: recommendedItem.id,
        type: recommendedItem.type,
        language: recommendedItem.language
      }, 'more'),
      showDescription: false,
      type: type
    }), /*#__PURE__*/_jsx(CloseRecommendationsButton, {
      closeType: "more",
      item: item,
      onClick: onClickCancel
    })]
  });
};
const MobileRecommendations = ({
  onClickCancel,
  item,
  onItemClick
}) => {
  const {
    id,
    language,
    type
  } = item;
  const {
    url,
    recommendedItem
  } = useMainRecommendationUrl(item);
  const {
    cardModels,
    cardModelsLoading
  } = useLearningContentCardModels({
    items: recommendedItem ? [recommendedItem] : [],
    skip: !recommendedItem
  });
  if (!url || !recommendedItem || cardModelsLoading || !(cardModels !== null && cardModels !== void 0 && cardModels.length)) return null;
  return /*#__PURE__*/_jsx(Flex, {
    blockSize: "100%",
    inlineSize: "100%",
    justifyContent: "center",
    padding: "1100",
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.recommendations.upNext"
        })
      }), /*#__PURE__*/_jsx(RecommendationsForLearningContent, {
        appearsIn: "more-video-recommendations-mobile",
        id: id,
        language: language,
        onItemClick: () => onItemClick(recommendedItem, 'mobile-main'),
        showDescription: false,
        size: "small",
        type: type,
        use: "on-dark"
      }), /*#__PURE__*/_jsxs(Flex, {
        gap: "300",
        marginBlockStart: "400",
        maxInlineSize: "60%",
        children: [/*#__PURE__*/_jsx(RouterButton, {
          onClick: () => onItemClick(recommendedItem, 'mobile-watch-now'),
          size: "small",
          to: url,
          use: "tertiary-light",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.recommendations.watchNow"
          })
        }), /*#__PURE__*/_jsx(CloseRecommendationsButton, {
          closeType: "mobile",
          item: item,
          onClick: onClickCancel
        })]
      })]
    })
  });
};
export const VideoRecommendationsForLearningContent = _ref2 => {
  let {
      moreTitle,
      autoplayTime = DEFAULT_AUTOPLAY_TIME,
      render = recommendationsContent => recommendationsContent,
      isInCollection = false
    } = _ref2,
    item = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    hideCompletedScreen
  } = useVideoPlayerContext();
  const [hasCanceled, setHasCanceled] = useState(false);
  const {
    isDesktop
  } = useMediaQuery();
  const handleCloseRecommendations = () => hideCompletedScreen();
  const handleOnItemClick = (recommendedItem, type) => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: `${type}-recommendation`,
      recommendationType: recommendedItem === null || recommendedItem === void 0 ? void 0 : recommendedItem.type,
      item: recommendedItem === null || recommendedItem === void 0 ? void 0 : recommendedItem.id,
      id: item.id,
      contentType: item.type,
      language: item.language
    });
    hideCompletedScreen();
  };
  const shouldShowMobileRecommendations = !isDesktop || isInCollection;
  if (shouldShowMobileRecommendations) {
    const mobileContent = /*#__PURE__*/_jsx(MobileRecommendations, {
      item: item,
      onClickCancel: handleCloseRecommendations,
      onItemClick: handleOnItemClick
    });

    // Still call render to allow tool cards to show in collections
    return /*#__PURE__*/_jsx(_Fragment, {
      children: render(mobileContent)
    });
  }

  // Desktop view for standalone content - shows MainRecommendation
  return /*#__PURE__*/_jsx(Flex, {
    blockSize: "100%",
    inlineSize: "100%",
    justifyContent: "center",
    children: render( /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      blockSize: "100%",
      flex: 1,
      flexDirection: "column",
      paddingBlockStart: hasCanceled ? '1500' : '1300',
      children: hasCanceled ? /*#__PURE__*/_jsx(MoreRecommendations, {
        item: item,
        moreTitle: moreTitle,
        onClickCancel: handleCloseRecommendations,
        onItemClick: handleOnItemClick
      }) : /*#__PURE__*/_jsx(MainRecommendation, {
        autoplayTime: autoplayTime,
        item: item,
        onClickCancel: () => setHasCanceled(true),
        onItemClick: handleOnItemClick
      })
    }))
  });
};
export default VideoRecommendationsForLearningContent;